import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, num, pct, pts, times } from "@owners/format";
import { api, usePrefs } from "../api";
import { useHotels } from "../components/Numbers";

/**
 * My Reports (SPEC §12): + New Report (builder with live preview; starts blank
 * every visit, nothing is remembered), My Saved Reports (archive of every
 * download with Search, reading panel and Redownload). My Template / Upload
 * Template is Phase 3 (PROPOSAL §9).
 */

export type Fmt = "text" | "money" | "money2" | "num" | "pct" | "ratio" | "pts" | "times";
interface Column { label: string; fmt: Fmt; variance?: boolean }
interface TableRow { cells: Array<string | number | null>; style?: "header" | "subtotal" | "total"; better?: "up" | "down" }
export type Block =
  | { kind: "cover"; key: string; title: string; lines: string[] }
  | { kind: "text"; key: string; title: string; paragraphs: string[] }
  | { kind: "kpis"; key: string; title: string; items: Array<{ label: string; value: number | null; fmt: Fmt; sub?: string }> }
  | { kind: "table"; key: string; title: string; columns: Column[]; rows: TableRow[]; note?: string }
  | { kind: "attachments"; key: string; title: string; items: Array<{ documentId: string; hotelName: string; label: string; fileName: string; source?: "receiver" }>; note?: string }
  | { kind: "unavailable"; key: string; title: string; reason: string }
  | { kind: "alert"; key: string; title: string; items: string[] };
export interface Model {
  title: string; rangeLabel: string; generatedAt: string; generatedBy: string; hotels: Array<{ id: string; name: string }>;
  scopes: Array<{ name: string; blocks: Block[] }>; keyFigures: Array<{ label: string; value: number | null; fmt: Fmt }>; summary: string;
}
interface Catalog { sections: Array<{ key: string; group: string; label: string; available: boolean; note?: string }>; lastMonth: string }

export function fmtCell(v: string | number | null | undefined, fmt: Fmt): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  switch (fmt) {
    case "money": return money(v);
    case "money2": return money(v, { decimals: 2 });
    case "num": return num(v);
    case "pct": return pct(v, { signed: true });
    case "ratio": return pct(v, { ratio: true });
    case "pts": return pts(v);
    case "times": return times(v);
    default: return String(v);
  }
}

export function MyReports() {
  // ?tab=receiver&id=… (the "shared with you" email link) opens a shared receiver report.
  const initial = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState<"new" | "saved" | "receiver">(initial.get("tab") === "receiver" ? "receiver" : "new");
  const [savedId, setSavedId] = useState<string | null>(initial.get("tab") === "receiver" ? initial.get("id") : null);
  // V014: receiver reports the special servicer shared with this level (Owner / Asset Manager / Receiver).
  const shared = useQuery({ queryKey: ["reports-saved", "/api/receiver-reports", ""], queryFn: () => api<{ reports: SavedRow[] }>("/api/receiver-reports/saved?q=") });
  const hasShared = (shared.data?.reports.length ?? 0) > 0;
  return (
    <>
      <div className="eyebrow">Owners Module · My Reports</div>
      <h1>My Reports</h1>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <div className="row">
          <button className={`btn ${tab === "new" ? "" : "secondary"}`} onClick={() => setTab("new")}>+ New Report</button>
          <button className={`btn ${tab === "saved" ? "" : "secondary"}`} onClick={() => { setSavedId(null); setTab("saved"); }}>My Saved Reports</button>
          {(hasShared || tab === "receiver") && <button className={`btn ${tab === "receiver" ? "" : "secondary"}`} onClick={() => { setSavedId(null); setTab("receiver"); }}>Receiver reports</button>}
        </div>
        <button className="btn secondary" disabled title="Templates (your own Word layout filled with Fyrehorse data) arrive in Phase 3">Upload Template</button>
      </div>
      {/* key: the builder starts blank every time it's opened (SPEC §12) */}
      {tab === "new" ? <Builder key="builder" onSaved={(id) => { setSavedId(id); setTab("saved"); }} />
        : tab === "receiver" ? <Saved base="/api/receiver-reports" selected={savedId} onSelect={setSavedId} empty="No receiver reports have been shared with you." />
        : <Saved selected={savedId} onSelect={setSavedId} />}
    </>
  );
}

function Builder({ onSaved }: { onSaved: (id: string) => void }) {
  const qc = useQueryClient();
  const hotels = useHotels().data?.hotels ?? [];
  const cat = useQuery({ queryKey: ["reports-catalog"], queryFn: () => api<Catalog>("/api/reports/catalog") });
  const [name, setName] = useState("");
  const [hotelIds, setHotelIds] = useState<string[]>([]);
  const [mode, setMode] = useState<"rollup" | "rollup_and_hotels">("rollup");
  const [kind, setKind] = useState<"month" | "ytd" | "t12" | "last_year" | "custom">("month");
  const [month, setMonth] = useState("");
  const [from, setFrom] = useState(""), [to, setTo] = useState("");
  const [sections, setSections] = useState<string[]>([]);
  const [summaryLength, setSummaryLength] = useState<"concise" | "expanded">("concise");
  const [compare, setCompare] = useState<string[]>([]);
  const prefs = usePrefs().data;
  const [formatPicked, setFormat] = useState<"pdf" | "xlsx" | "both" | null>(null);
  const format = formatPicked ?? prefs?.reportFormat ?? "pdf";               // Settings → Reports & delivery
  const lastMonth = cat.data?.lastMonth ?? "";

  const settings = useMemo(() => ({
    name: name || "Untitled report", hotelIds, mode, range: { kind, month: month || undefined, from, to }, sections, compare, format, summaryLength,
  }), [name, hotelIds, mode, kind, month, from, to, sections, compare, format, summaryLength]);
  const ready = hotelIds.length > 0 && sections.length > 0 && (kind !== "custom" || (from && to));

  // Live preview, debounced.
  const [debounced, setDebounced] = useState(settings);
  useEffect(() => { const h = setTimeout(() => setDebounced(settings), 500); return () => clearTimeout(h); }, [settings]);
  const preview = useQuery({
    queryKey: ["report-preview", debounced], enabled: !!ready,
    queryFn: () => api<Model>("/api/reports/preview", { method: "POST", body: debounced }),
    staleTime: 60_000,
  });

  const build = useMutation({
    mutationFn: () => api<{ id: string; pdf: boolean; xlsx: boolean }>("/api/reports/build", { method: "POST", body: { ...settings, name: name.trim() } }),
    onSuccess: (r) => {
      if (r.pdf) window.location.assign(`/api/reports/saved/${r.id}/file?format=pdf`);
      if (r.xlsx) setTimeout(() => window.location.assign(`/api/reports/saved/${r.id}/file?format=xlsx`), r.pdf ? 900 : 0);
      qc.invalidateQueries({ queryKey: ["reports-saved"] });
      setTimeout(() => onSaved(r.id), 1500);
    },
  });

  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const groups = [...new Set((cat.data?.sections ?? []).map((s) => s.group))];

  return (
    <div className="row" style={{ alignItems: "flex-start", gap: 18, flexWrap: "nowrap" }}>
      <div className="card" style={{ width: 380, flex: "none" }}>
        <Step n={1} title="Report name" done={!!name.trim()}><input style={{ width: "100%" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. September owner package" /></Step>
        <Step n={2} title="Hotels" done={hotelIds.length > 0}>
          <div className="checklist">
            <label><input type="checkbox" checked={hotelIds.length === hotels.length && hotels.length > 0} onChange={(e) => setHotelIds(e.target.checked ? hotels.map((h) => h.id) : [])} /> <strong>All {hotels.length}</strong></label>
            {hotels.map((h) => <label key={h.id}><input type="checkbox" checked={hotelIds.includes(h.id)} onChange={() => toggle(hotelIds, setHotelIds, h.id)} /> {h.name}</label>)}
          </div>
          {hotelIds.length > 1 && (
            <div className="row" style={{ marginTop: 6, fontSize: 13 }}>
              <label><input type="radio" checked={mode === "rollup"} onChange={() => setMode("rollup")} /> Combined rollup</label>
              <label><input type="radio" checked={mode === "rollup_and_hotels"} onChange={() => setMode("rollup_and_hotels")} /> Rollup + each hotel</label>
            </div>
          )}
        </Step>
        <Step n={3} title="Date range" done={kind !== "custom" || !!(from && to)}>
          <div className="seg" style={{ marginBottom: 6 }}>
            {([["month", "Month"], ["ytd", "YTD"], ["t12", "T12"], ["last_year", "Last year"], ["custom", "Custom"]] as const).map(([k, l]) =>
              <button key={k} className={kind === k ? "on" : ""} onClick={() => setKind(k)}>{l}</button>)}
          </div>
          {kind === "custom" ? (
            <div className="row" style={{ fontSize: 13 }}>
              <input type="month" value={from} max={lastMonth} onChange={(e) => setFrom(e.target.value)} /> to
              <input type="month" value={to} max={lastMonth} onChange={(e) => setTo(e.target.value)} />
            </div>
          ) : kind !== "last_year" && (
            <label className="muted" style={{ fontSize: 13 }}>{kind === "month" ? "Month" : "Through"} <input type="month" value={month || lastMonth} max={lastMonth} onChange={(e) => setMonth(e.target.value)} /></label>
          )}
        </Step>
        <Step n={4} title="What to include" done={sections.length > 0}
          aside={<span className="muted">{sections.length} of {(cat.data?.sections ?? []).length}</span>}>
          {groups.map((g) => {
            const avail = (cat.data?.sections ?? []).filter((s) => s.group === g && s.available).map((s) => s.key);
            const on = avail.filter((k) => sections.includes(k)).length;
            return (
            <div key={g} className="rep-group">
              <div className="rep-group-head">
                <span className="eyebrow">{g}</span>
                {avail.length > 1 && (
                  <button className="link" onClick={() => setSections(on === avail.length ? sections.filter((k) => !avail.includes(k)) : [...new Set([...sections, ...avail])])}>
                    {on === avail.length ? "none" : "all"}
                  </button>
                )}
                <span className="muted" style={{ marginLeft: "auto", fontSize: 11 }}>{on}/{avail.length}</span>
              </div>
              {(cat.data?.sections ?? []).filter((s) => s.group === g).map((s) => (
                <label key={s.key} style={{ display: "flex", gap: 6, fontSize: 13, color: s.available ? undefined : "var(--muted)" }} title={s.note}>
                  <input type="checkbox" disabled={!s.available} checked={sections.includes(s.key)} onChange={() => toggle(sections, setSections, s.key)} />
                  <span>{s.label}{s.note && <span className="muted"> · {s.note}</span>}</span>
                </label>
              ))}
              {g === "Summary" && sections.includes("exec_summary") && (
                <div className="seg" style={{ margin: "4px 0 0 22px" }}>
                  <button className={summaryLength === "concise" ? "on" : ""} onClick={() => setSummaryLength("concise")}>Concise</button>
                  <button className={summaryLength === "expanded" ? "on" : ""} onClick={() => setSummaryLength("expanded")}>Expanded</button>
                </div>
              )}
            </div>
            );
          })}
        </Step>
        <Step n={5} title="Compare against" done={compare.length > 0} optional>
          <div className="row" style={{ fontSize: 13 }}>
            {([["budget", "Budget"], ["reforecast", "Reforecast"], ["last_year", "Last year"]] as const).map(([k, l]) =>
              <label key={k}><input type="checkbox" checked={compare.includes(k)} onChange={() => toggle(compare, setCompare, k)} /> {l}</label>)}
          </div>
        </Step>
        <Step n={6} title="File format" done>
          <div className="seg">
            {([["pdf", "PDF"], ["xlsx", "Excel"], ["both", "PDF + Excel"]] as const).map(([k, l]) =>
              <button key={k} className={format === k ? "on" : ""} onClick={() => setFormat(k)}>{l}</button>)}
          </div>
        </Step>
        {build.error && <p className="error">{(build.error as Error).message}</p>}
        <button className="btn gold" style={{ width: "100%", marginTop: 8 }} disabled={!ready || !name.trim() || build.isPending} onClick={() => build.mutate()}>
          {build.isPending ? "Building…" : "Download"}
        </button>
        {!name.trim() && ready && <p className="muted" style={{ fontSize: 12 }}>Name the report to download it.</p>}
      </div>

      <div className="card report-preview" style={{ flex: 1, minWidth: 0, maxHeight: "80vh", overflowY: "auto" }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Live preview</div>
        {!ready ? <p className="muted">Pick hotels and at least one section to see the report.</p>
          : preview.isLoading ? <p className="muted">Building preview…</p>
          : preview.error ? <p className="error">{(preview.error as Error).message}</p>
          : preview.data && <ReportView m={preview.data} />}
      </div>
    </div>
  );
}

/** Demo's numbered steps: gold badge, a tick once the step is filled in. */
function Step({ n, title, done, optional, aside, children }: { n: number; title: string; done?: boolean; optional?: boolean; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="rep-step">
      <div className="rep-step-head">
        <span className={`rep-step-n${done ? " done" : ""}`}>{done ? "✓" : n}</span>
        <span className="rep-step-title">{title}</span>
        {optional && !done && <span className="muted" style={{ fontSize: 11 }}>optional</span>}
        {aside && <span style={{ marginLeft: "auto", fontSize: 12 }}>{aside}</span>}
      </div>
      <div className="rep-step-body">{children}</div>
    </div>
  );
}

/** The report as it will download — used for the live preview and the saved-report reading panel. */
export function ReportView({ m }: { m: Model }) {
  return (
    <div>
      {m.scopes.map((sc, si) => (
        <div key={si} style={{ marginBottom: 24 }}>
          {m.scopes.length > 1 && <h2 style={{ borderBottom: "2px solid var(--gold)", paddingBottom: 4 }}>{sc.name}</h2>}
          {sc.blocks.map((b, bi) => <BlockView key={bi} b={b} />)}
        </div>
      ))}
    </div>
  );
}

function BlockView({ b }: { b: Block }) {
  if (b.kind === "cover") return (
    <div style={{ textAlign: "center", padding: "28px 0", borderBottom: "1px solid var(--line)", marginBottom: 16 }}>
      <div style={{ fontFamily: "var(--serif)", fontSize: 26, color: "var(--navy)" }}>{b.title}</div>
      {b.lines.map((l, i) => <div key={i} className="muted">{l}</div>)}
    </div>
  );
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 6 }}>{b.title}</h3>
      {b.kind === "text" && b.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      {b.kind === "unavailable" && <p className="muted">{b.reason}</p>}
      {b.kind === "alert" && (
        <div style={{ border: "1.5px solid var(--bad)", background: "#fdf0ef", borderRadius: 6, padding: "8px 12px", color: "var(--bad)" }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{b.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
        </div>
      )}
      {b.kind === "kpis" && (
        <div className="tiles" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
          {b.items.map((it) => (
            <div key={it.label} className="card tile">
              <div className="eyebrow">{it.label}</div>
              <div className="tile-value" style={{ fontSize: 22 }}>{it.value === null ? <span className="tbd">TBD</span> : fmtCell(it.value, it.fmt)}</div>
              {it.sub && <div className="tile-sub">{it.sub}</div>}
            </div>
          ))}
        </div>
      )}
      {b.kind === "table" && (
        <div style={{ overflowX: "auto" }}>
          <table className="fin">
            <thead><tr>{b.columns.map((c, i) => <th key={i} className={i && c.fmt !== "text" ? "num" : ""}>{c.label}</th>)}</tr></thead>
            <tbody>{b.rows.map((r, ri) => r.style === "header"
              ? <tr key={ri} className="k-header"><td colSpan={b.columns.length}>{r.cells[0]}</td></tr>
              : <tr key={ri} className={r.style ? `k-${r.style}` : ""}>{r.cells.map((v, i) => {
                  const c = b.columns[i]!;
                  const tone = c.variance && typeof v === "number" && r.better && Math.abs(v) >= 0.005 ? ((v > 0) === (r.better === "up") ? "good" : "bad") : "";
                  return <td key={i} className={`${i && c.fmt !== "text" ? "num" : ""} ${tone}`}>{i === 0 ? v : fmtCell(v, c.fmt)}</td>;
                })}</tr>)}</tbody>
          </table>
          {b.note && <p className="muted" style={{ fontSize: 12 }}>{b.note}</p>}
        </div>
      )}
      {b.kind === "attachments" && (
        <>
          {b.note && <p className="muted" style={{ marginTop: 0 }}>{b.note}</p>}
          {b.items.length > 0 && <ul style={{ margin: 0 }}>{b.items.map((it) => <li key={(it.source ?? "") + it.documentId}>{it.hotelName} · {it.label} · {it.fileName}</li>)}</ul>}
        </>
      )}
    </div>
  );
}

interface SavedRow { id: string; name: string; hotelNames: string[]; periodLabel: string; createdAt: string; pdf: boolean; xlsx: boolean; sharedWith?: string[] }
const SHARE_LABELS: Record<string, string> = { owner: "Owner", asset_manager: "Asset Manager", receiver: "Receiver" };

/** V014: who else may open this receiver report (servicer's archive only). */
function Sharing({ base, id, sharedWith, shareable }: { base: string; id: string; sharedWith: string[]; shareable: string[] }) {
  const qc = useQueryClient();
  const [sel, setSel] = useState<string[]>(sharedWith);
  useEffect(() => setSel(sharedWith), [id, sharedWith.join(",")]);
  const save = useMutation({
    mutationFn: (next: string[]) => api<{ sharedWith: string[] }>(`${base}/saved/${id}/sharing`, { method: "PUT", body: { sharedWith: next } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["report-saved", base, id] }); qc.invalidateQueries({ queryKey: ["reports-saved", base] }); },
  });
  const dirty = sel.slice().sort().join(",") !== sharedWith.slice().sort().join(",");
  return (
    <div className="card" style={{ marginTop: 14, background: "var(--cream)" }}>
      <div className="eyebrow">Who can see this report</div>
      <p className="muted" style={{ margin: "4px 0 8px" }}>Only you, unless you tick a level below. They see it under My Reports → Receiver reports, for this hotel only, and get an email. Court and lender copies go out as the PDF.</p>
      <div className="row">
        {shareable.map((l) => (
          <label key={l} className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={sel.includes(l)} onChange={(e) => setSel(e.target.checked ? [...sel, l] : sel.filter((x) => x !== l))} /> {SHARE_LABELS[l] ?? l}
          </label>
        ))}
        <button className="btn" disabled={!dirty || save.isPending} onClick={() => save.mutate(sel)}>{save.isPending ? "Saving…" : "Save sharing"}</button>
        {save.isError && <span className="error">{(save.error as Error).message}</span>}
        {!dirty && sharedWith.length > 0 && <span className="muted">Shared with {sharedWith.map((l) => SHARE_LABELS[l] ?? l).join(", ")}</span>}
      </div>
    </div>
  );
}

/** The archive (search + reading panel + Redownload). `base` = /api/reports (My Reports) or /api/receivers/reports. */
export function Saved({ selected, onSelect, base = "/api/reports", empty = "Reports you download are kept here." }: { selected: string | null; onSelect: (id: string) => void; base?: string; empty?: string }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const h = setTimeout(() => setDq(q), 300); return () => clearTimeout(h); }, [q]);
  const list = useQuery({ queryKey: ["reports-saved", base, dq], queryFn: () => api<{ reports: SavedRow[] }>(`${base}/saved?q=${encodeURIComponent(dq)}`) });
  const rows = list.data?.reports ?? [];
  const current = selected ?? rows[0]?.id ?? null;
  const detail = useQuery({ queryKey: ["report-saved", base, current], enabled: !!current,
    queryFn: () => api<{ id: string; createdAt: string; pdf: boolean; xlsx: boolean; report: Model; sharedWith?: string[]; shareable?: string[] }>(`${base}/saved/${current}`) });
  const d = detail.data;

  return (
    <div className="row" style={{ alignItems: "flex-start", gap: 18, flexWrap: "nowrap" }}>
      <div className="card" style={{ width: 340, flex: "none" }}>
        <input style={{ width: "100%", marginBottom: 10 }} placeholder="Search name, hotel, period or date" value={q} onChange={(e) => setQ(e.target.value)} />
        {list.isLoading ? <p className="muted">Loading…</p> : rows.length === 0 ? <p className="muted">{dq ? "Nothing matches." : empty}</p> : (
          <div>{rows.map((r) => (
            <div key={r.id} onClick={() => onSelect(r.id)} style={{ padding: "8px 6px", borderBottom: "1px solid var(--line)", cursor: "pointer", background: r.id === current ? "var(--cream)" : undefined }}>
              <div style={{ fontWeight: 600 }}>{r.name}{r.sharedWith && r.sharedWith.length > 0 && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> · shared</span>}</div>
              <div className="muted" style={{ fontSize: 12 }}>{r.periodLabel} · {r.hotelNames.length === 1 ? r.hotelNames[0] : `${r.hotelNames.length} hotels`}</div>
              <div className="muted" style={{ fontSize: 11 }}>{new Date(r.createdAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</div>
            </div>
          ))}</div>
        )}
      </div>
      <div className="card" style={{ flex: 1, minWidth: 0, maxHeight: "80vh", overflowY: "auto" }}>
        {!d ? <p className="muted">{current ? "Loading…" : "Pick a report."}</p> : (
          <>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <h2 style={{ margin: 0 }}>{d.report.title}</h2>
                <div className="muted">{d.report.rangeLabel} · {d.report.hotels.length === 1 ? d.report.hotels[0]!.name : `${d.report.hotels.length} hotels`} · made {new Date(d.createdAt).toLocaleDateString("en-US", { dateStyle: "medium" })} by {d.report.generatedBy}</div>
              </div>
              <div className="row">
                {d.pdf && <a className="btn" href={`${base}/saved/${d.id}/file?format=pdf`}>Redownload PDF</a>}
                {d.xlsx && <a className="btn secondary" href={`${base}/saved/${d.id}/file?format=xlsx`}>Redownload Excel</a>}
              </div>
            </div>
            <div className="tiles" style={{ marginTop: 14 }}>
              {d.report.keyFigures.map((k) => (
                <div key={k.label} className="card tile"><div className="eyebrow">{k.label}</div><div className="tile-value" style={{ fontSize: 22 }}>{k.value === null ? <span className="tbd">TBD</span> : fmtCell(k.value, k.fmt)}</div></div>
              ))}
            </div>
            {d.shareable && <Sharing base={base} id={d.id} sharedWith={d.sharedWith ?? []} shareable={d.shareable} />}
            <p>{d.report.summary}</p>
            <div className="eyebrow">Sections</div>
            <p className="muted" style={{ marginTop: 4 }}>{[...new Set(d.report.scopes.flatMap((s) => s.blocks.map((b) => b.title)))].join(" · ")}</p>
            <details><summary className="muted" style={{ cursor: "pointer" }}>Show the full report</summary><ReportView m={d.report} /></details>
          </>
        )}
      </div>
    </div>
  );
}
